/**
 * Chat Socket.io Context
 * Manages Socket.io connection for real-time chat
 */

import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import api from '../config/api';

interface UserPresence {
  user_id: string;
  status: 'online' | 'away' | 'busy' | 'offline';
  custom_status?: string;
  status_emoji?: string;
  last_seen?: string;
}

interface ChatSocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  isConnecting: boolean;
  connectionError: string | null;
  userPresence: Record<string, UserPresence>;
  connect: () => void;
  disconnect: () => void;
  updatePresence: (status: 'online' | 'away' | 'busy' | 'offline') => void;
  reconnect: () => void;
}

const ChatSocketContext = createContext<ChatSocketContextType>({
  socket: null,
  isConnected: false,
  isConnecting: false,
  connectionError: null,
  userPresence: {},
  connect: () => {},
  disconnect: () => {},
  updatePresence: () => {},
  reconnect: () => {},
});

export const useChatSocket = () => {
  const context = useContext(ChatSocketContext);
  if (!context) {
    throw new Error('useChatSocket must be used within ChatSocketProvider');
  }
  return context;
};

interface ChatSocketProviderProps {
  children: React.ReactNode;
}

export const ChatSocketProvider: React.FC<ChatSocketProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [userPresence, setUserPresence] = useState<Record<string, UserPresence>>({});
  const socketRef = useRef<Socket | null>(null);
  const awayTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);
  const maxReconnectAttempts = 5;

  const connect = () => {
    if (socketRef.current?.connected) {
      console.log('[Chat Socket] Already connected');
      return; // Already connected
    }

    if (!user) {
      console.log('[Chat Socket] No user, cannot connect');
      return; // No user, can't connect
    }

    // Get auth token from localStorage or API
    const token = localStorage.getItem('token');

    if (!token) {
      console.warn('[Chat Socket] No auth token available');
      setConnectionError('No authentication token');
      return;
    }

    setIsConnecting(true);
    setConnectionError(null);

    // Create Socket.io connection to /chat namespace
    // Use the same logic as API config
    const isProduction = window.location.hostname === 'patelhrms.com' || window.location.hostname.includes('patelhrms.com');
    const socketUrl = isProduction ? window.location.origin : 'http://localhost:5000';
    // Socket.io automatically prepends /socket.io to the namespace
    // So io('/chat') connects to /socket.io/chat
    console.log('[Chat Socket] Connecting to:', `${socketUrl}`, 'namespace: /chat');
    
    const newSocket = io(`${socketUrl}/chat`, {
      auth: {
        token: token,
      },
      // Try polling first, then upgrade to websocket if available
      // This works better with IIS URL Rewrite
      transports: ['polling', 'websocket'],
      upgrade: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 10,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      // Force polling if WebSocket fails
      forceNew: false,
    });

    newSocket.on('connect', () => {
      console.log('[Chat Socket] Connected to chat server');
      setIsConnected(true);
      setIsConnecting(false);
      setConnectionError(null);
      reconnectAttemptsRef.current = 0; // Reset reconnect attempts on successful connection
      
      // Clear any reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      // Set status to online when connected
      if (user) {
        newSocket.emit('presence:update', { status: 'online' });
        // Update local state immediately
        setUserPresence((prev) => ({
          ...prev,
          [user.id]: {
            user_id: user.id,
            status: 'online',
            last_seen: new Date().toISOString(),
          },
        }));
      }
    });

    newSocket.on('disconnect', (reason) => {
      console.log('[Chat Socket] Disconnected from chat server, reason:', reason);
      setIsConnected(false);
      setIsConnecting(false);
      
      // Set status to offline when disconnected
      if (user) {
        setUserPresence((prev) => ({
          ...prev,
          [user.id]: {
            user_id: user.id,
            status: 'offline',
            last_seen: new Date().toISOString(),
          },
        }));
      }
      
      // If disconnected due to error, try to reconnect after delay (but limit attempts)
      if ((reason === 'io server disconnect' || reason === 'transport close') && reconnectAttemptsRef.current < maxReconnectAttempts) {
        console.log('[Chat Socket] Will attempt to reconnect... (attempt', reconnectAttemptsRef.current + 1, 'of', maxReconnectAttempts, ')');
        reconnectAttemptsRef.current += 1;
        reconnectTimeoutRef.current = setTimeout(() => {
          if (!socketRef.current?.connected && user) {
            console.log('[Chat Socket] Attempting manual reconnect...');
            connect();
          }
        }, 5000); // Increased delay to prevent rapid reconnection
      } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
        console.log('[Chat Socket] Max reconnection attempts reached. Stopping automatic reconnection.');
        setConnectionError('Connection failed after multiple attempts. Please refresh the page.');
      }
    });

    newSocket.on('connect_error', (error) => {
      console.error('[Chat Socket] Connection error:', error.message);
      console.error('[Chat Socket] Error details:', error);
      setIsConnected(false);
      setIsConnecting(false);
      
      // Provide more user-friendly error messages
      let errorMessage = error.message;
      if (error.message.includes('websocket error') || error.message.includes('xhr poll error')) {
        errorMessage = 'Unable to connect to chat server. Please check your connection.';
      } else if (error.message.includes('Authentication')) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.message.includes('CORS')) {
        errorMessage = 'Connection blocked. Please contact administrator.';
      }
      
      setConnectionError(errorMessage);
      
      // Try to reconnect after delay (only for non-auth errors and if under max attempts)
      if (!error.message.includes('Authentication') && reconnectAttemptsRef.current < maxReconnectAttempts) {
        reconnectAttemptsRef.current += 1;
        console.log('[Chat Socket] Will retry connection (attempt', reconnectAttemptsRef.current, 'of', maxReconnectAttempts, ')');
        reconnectTimeoutRef.current = setTimeout(() => {
          if (!socketRef.current?.connected && user) {
            console.log('[Chat Socket] Retrying connection after error...');
            connect();
          }
        }, 5000);
      } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
        console.log('[Chat Socket] Max reconnection attempts reached. Stopping automatic reconnection.');
        setConnectionError('Connection failed after multiple attempts. Please refresh the page or try again later.');
      }
    });

    // Listen for presence updates
    newSocket.on('presence:update', (data: UserPresence) => {
      console.log(`🔵 STATUS UPDATE CLIENT: Received broadcast from server:`, data);
      setUserPresence((prev) => {
        const updated = {
          ...prev,
          [data.user_id]: data,
        };
        console.log(`🔵 STATUS UPDATE CLIENT: Updated userPresence for user ${data.user_id}:`, updated[data.user_id]);
        return updated;
      });
    });

    socketRef.current = newSocket;
    setSocket(newSocket);

    // Set up away detection (after 5 minutes of inactivity)
    const handleActivity = () => {
      if (awayTimeoutRef.current) {
        clearTimeout(awayTimeoutRef.current);
      }
      if (newSocket.connected && user) {
        newSocket.emit('presence:update', { status: 'online' });
        awayTimeoutRef.current = setTimeout(() => {
          if (newSocket.connected && user) {
            newSocket.emit('presence:update', { status: 'away' });
          }
        }, 5 * 60 * 1000); // 5 minutes
      }
    };

    // Track user activity
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keypress', handleActivity);
    window.addEventListener('click', handleActivity);
    handleActivity(); // Initial call

    // Store cleanup function
    (newSocket as any)._activityCleanup = () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keypress', handleActivity);
      window.removeEventListener('click', handleActivity);
    };
  };

  const disconnect = () => {
    // Stop all reconnection attempts
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    reconnectAttemptsRef.current = 0; // Reset reconnect attempts
    
    if (socketRef.current) {
      // Clean up activity listeners
      if ((socketRef.current as any)._activityCleanup) {
        (socketRef.current as any)._activityCleanup();
      }
      socketRef.current.disconnect();
      socketRef.current = null;
      setSocket(null);
      setIsConnected(false);
      setIsConnecting(false);
      setConnectionError(null);
    }
    if (awayTimeoutRef.current) {
      clearTimeout(awayTimeoutRef.current);
      awayTimeoutRef.current = null;
    }
  };

  const updatePresence = (status: 'online' | 'away' | 'busy' | 'offline') => {
    if (socketRef.current?.connected && user) {
      console.log(`🔵 STATUS UPDATE CLIENT: Sending status="${status}" to server`);
      socketRef.current.emit('presence:update', { status });
      // Update local state immediately for instant feedback
      setUserPresence((prev) => {
        const updated = {
          ...prev,
          [user.id]: {
            user_id: user.id,
            status: status,
            last_seen: new Date().toISOString(),
          },
        };
        console.log(`🔵 STATUS UPDATE CLIENT: Local state updated to:`, updated[user.id]);
        return updated;
      });
    } else {
      console.error('❌ STATUS UPDATE CLIENT ERROR - Socket connected:', socketRef.current?.connected, 'User:', !!user);
    }
  };

  const reconnect = () => {
    reconnectAttemptsRef.current = 0; // Reset attempts
    setConnectionError(null);
    disconnect();
    // Small delay before reconnecting
    setTimeout(() => {
      if (user) {
        connect();
      }
    }, 500);
  };

  useEffect(() => {
    // Only connect if user is authenticated and token exists
    const token = localStorage.getItem('token');
    
    if (user && token) {
      connect();
    } else {
      // Disconnect if no user or no token
      disconnect();
    }

    return () => {
      // Cleanup on unmount or when user/token changes
      disconnect();
    };
  }, [user]); // Re-run when user changes (login/logout)

  const value: ChatSocketContextType = {
    socket,
    isConnected,
    isConnecting,
    connectionError,
    userPresence,
    connect,
    disconnect,
    updatePresence,
    reconnect,
  };

  return (
    <ChatSocketContext.Provider value={value}>
      {children}
    </ChatSocketContext.Provider>
  );
};

export default ChatSocketContext;

